import React from 'react';
import { CheckCircle, FileText, Shield, Award, Clock, TrendingUp } from 'lucide-react';
import ServiceFAQ from '../../components/ServiceFAQ';

const AntiCorruption = ({ onGetStarted }) => {
  const faqs = [
    { question: 'What is the Foreign Corrupt Practices Act (FCPA)?', answer: 'The FCPA is a U.S. federal law prohibiting bribery of foreign officials for business purposes. It applies to U.S. companies, foreign companies listed on U.S. exchanges, and their employees, agents, and partners worldwide.' },
    { question: 'Who needs an anti-corruption compliance program?', answer: 'Any business operating internationally or working with foreign officials should have an anti-corruption program. The program scope depends on risk factors including geographic operations, industry, and business model.' },
    { question: 'What are the consequences of FCPA violations?', answer: 'Violations can result in substantial fines, imprisonment, debarment from government contracts, and reputational damage. Both companies and individuals can be prosecuted. Recent enforcement actions have resulted in fines exceeding hundreds of millions.' },
    { question: 'How do you conduct internal investigations?', answer: 'We conduct confidential investigations including witness interviews, document review, and forensic analysis. We maintain attorney-client privilege throughout and provide reports with recommendations for remedial actions.' },
    { question: 'What is third-party due diligence?', answer: 'Due diligence involves vetting agents, distributors, and partners for corruption risks. This includes background checks, reputation reviews, and risk assessments to ensure third parties comply with anti-corruption laws.' },
  ];

  return (
    <div className="pt-20">
      <section className="py-24 md:py-32 relative overflow-hidden" style={{ backgroundImage: 'url(https://images.pexels.com/photos/5668774/pexels-photo-5668774.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940)', backgroundSize: 'cover', backgroundPosition: 'center' }}>
        <div className="absolute inset-0 bg-[#2C3E50]/80"></div>
        <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
          <div className="max-w-3xl">
            <div className="text-sm uppercase tracking-[0.2em] font-medium text-white/90 mb-4">Compliance & Protection Services</div>
            <h1 className="text-5xl lg:text-6xl tracking-tight leading-none text-white font-bold mb-6">Anti-Corruption Compliance</h1>
            <p className="text-xl text-white/90 leading-relaxed mb-8">FCPA compliance, risk assessment, and internal investigations. Protect your business from corruption risks.</p>
            <button onClick={onGetStarted} className="bg-[#CA9A67] text-white hover:bg-[#b8894f] transition-colors px-8 py-4 font-medium">Get Started</button>
          </div>
        </div>
      </section>

      <section className="py-24 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-3xl lg:text-4xl tracking-tight text-[#2E2E2E] font-bold mb-6">Overview</h2>
              <p className="text-base leading-relaxed text-[#2E2E2E]/80">Anti-corruption compliance is critical for businesses operating globally. We assist with FCPA compliance, anti-bribery programs, internal investigations, and regulatory matters.</p>
              <h3 className="text-xl lg:text-2xl text-[#2C3E50] font-semibold mb-6 mt-8">Key Benefits</h3>
              <ul className="space-y-4">
                {['FCPA and anti-bribery compliance programs', 'Risk assessments and due diligence', 'Internal investigations', 'Training and policy development', 'Regulatory defense and investigations', 'Third-party risk management'].map((benefit, i) => (
                  <li key={i} className="flex items-start space-x-3">
                    <CheckCircle className="w-5 h-5 text-[#2C3E50] mt-0.5 flex-shrink-0" strokeWidth={1.5} />
                    <span className="text-base text-[#2E2E2E]/80">{benefit}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <img src="https://images.pexels.com/photos/19825346/pexels-photo-19825346.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940" alt="Anti-corruption compliance" className="w-full h-[450px] object-cover shadow-lg" />
            </div>
          </div>
        </div>
      </section>

      <section className="py-24 md:py-32 bg-[#F8F9FA]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl tracking-tight text-[#2E2E2E] font-bold mb-4">Compliance Services</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mobile-swiper">
            {['FCPA Compliance', 'Anti-Bribery Programs', 'Risk Assessments', 'Internal Investigations', 'Compliance Training', 'Policy Development', 'Due Diligence', 'Regulatory Defense'].map((service, i) => (
              <div key={i} className="bg-white border border-[#2C3E50]/5 shadow-sm p-6">
                <FileText className="w-8 h-8 text-[#CA9A67] mb-3" strokeWidth={1.5} />
                <h4 className="font-semibold text-[#2C3E50]">{service}</h4>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 md:py-32 relative overflow-hidden" style={{ backgroundImage: 'url(https://images.pexels.com/photos/5668858/pexels-photo-5668858.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=750&w=1260)', backgroundSize: 'cover', backgroundPosition: 'center', backgroundAttachment: 'fixed' }}>
        <div className="absolute inset-0 bg-[#2C3E50]/90"></div>
        <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
          <div className="text-center mb-16">
            <div className="text-sm uppercase tracking-[0.2em] font-medium text-[#CA9A67] mb-4">Our Approach</div>
            <h2 className="text-3xl lg:text-4xl tracking-tight text-white font-bold mb-4">Our Process</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mobile-swiper">
            {[
              { title: 'Risk Assessment', description: 'Identify corruption risks in your operations and markets.' },
              { title: 'Program Development', description: 'Design comprehensive anti-corruption compliance program.' },
              { title: 'Implementation', description: 'Deploy policies, training, and monitoring systems.' },
              { title: 'Ongoing Support', description: 'Continuous compliance monitoring and program updates.' },
            ].map((step, i) => (
              <div key={i} className="bg-white/5 backdrop-blur-sm border border-white/10 p-6 hover:bg-white/10 transition-all duration-300">
                <div className="text-4xl font-bold text-[#CA9A67] mb-4">{i + 1}</div>
                <h3 className="text-xl text-white font-semibold mb-3">{step.title}</h3>
                <p className="text-sm text-white/70 leading-relaxed">{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 md:py-32 bg-[#F8F9FA]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <img src="https://images.pexels.com/photos/8296977/pexels-photo-8296977.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940" alt="Why choose our anti-corruption team" className="w-full h-[500px] object-cover shadow-lg" />
            </div>
            <div>
              <h2 className="text-3xl lg:text-4xl tracking-tight text-[#2E2E2E] font-bold mb-8">Why Choose Us</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mobile-swiper">
                {[
                  { icon: Award, title: 'FCPA Expertise', description: 'Deep knowledge of anti-corruption regulations.' },
                  { icon: Shield, title: 'Risk Protection', description: 'Comprehensive programs to prevent violations.' },
                  { icon: Clock, title: 'Responsive Support', description: 'Immediate assistance for compliance issues.' },
                  { icon: TrendingUp, title: 'Global Coverage', description: 'International anti-corruption compliance experience.' },
                ].map((item, i) => {
                  const Icon = item.icon;
                  return (
                    <div key={i} className="bg-white border border-[#2C3E50]/5 shadow-sm p-6">
                      <div className="inline-flex items-center justify-center w-12 h-12 bg-[#CA9A67] mb-4">
                        <Icon className="w-6 h-6 text-white" strokeWidth={1.5} />
                      </div>
                      <h3 className="text-lg text-[#2C3E50] font-semibold mb-2">{item.title}</h3>
                      <p className="text-sm text-[#2E2E2E]/70 leading-relaxed">{item.description}</p>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </section>

      <ServiceFAQ faqs={faqs} />

      <section className="py-24 md:py-32 relative overflow-hidden" style={{ backgroundImage: 'url(https://images.pexels.com/photos/5668473/pexels-photo-5668473.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940)', backgroundSize: 'cover', backgroundPosition: 'center' }}>
        <div className="absolute inset-0 bg-[#2C3E50]/85"></div>
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center relative z-10">
          <h2 className="text-3xl lg:text-4xl tracking-tight text-white font-bold mb-6">Need Compliance Assistance?</h2>
          <p className="text-xl text-white/90 leading-relaxed mb-8">Contact us for anti-corruption compliance services.</p>
          <button onClick={onGetStarted} className="bg-[#CA9A67] text-white hover:bg-[#b8894f] transition-colors px-8 py-4 font-medium">Get Legal Help Now</button>
        </div>
      </section>
    </div>
  );
};

export default AntiCorruption;
